import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { CustomReport } from "../../../slices/CustomReportsSlice";
import { StandardView, ViewFilters, viewLabels } from "./savedView";

/**
 * Every standard report runs inside this provider (ReportsView). It starts the report's
 * filters and choices from a saved view (`/reports/saved/:id`) and keeps the current ones,
 * so ReportPage can offer "Save as my report" / "Save changes".
 */

/** Reports that have a date filter (the others ignore `preset`). */
const DATE_REPORTS = [
  "time",
  "completed",
  "revenue",
  "utilization",
  "profitability",
  "payments",
];

export type SavedMeta = Pick<
  CustomReport,
  "id" | "name" | "mine" | "shared" | "createdByName"
>;

type Current = {
  filters: ViewFilters;
  extras: Record<string, string>;
  extraLabels: Record<string, string>;
};

type Ctx = {
  report: string;
  initial: StandardView | null;
  saved: SavedMeta | null;
  current: Current;
  setFilters: (filters: ViewFilters) => void;
  setExtra: (key: string, value: string, label: string) => void;
  /** Back to the saved filters (Undo). */
  reset: () => void;
  /** The view to store, as now on screen. */
  snapshot: () => StandardView;
};

const SavedViewContext = createContext<Ctx | null>(null);

export const useSavedView = () => useContext(SavedViewContext);

const EMPTY: ViewFilters = {
  preset: "",
  userIds: [],
  clientIds: [],
  workTypeIds: [],
};

export const SavedViewProvider: FC<{
  report: string;
  initial?: StandardView | null;
  saved?: SavedMeta | null;
  children: ReactNode;
}> = ({ report, initial = null, saved = null, children }) => {
  const [version, setVersion] = useState(0);
  const [current, setCurrent] = useState<Current>({
    filters: initial?.filters ?? EMPTY,
    extras: initial?.extras ?? {},
    extraLabels: {},
  });
  const setFilters = useCallback(
    (filters: ViewFilters) => setCurrent((c) => ({ ...c, filters })),
    [],
  );
  const setExtra = useCallback(
    (key: string, value: string, label: string) =>
      setCurrent((c) =>
        c.extras[key] === value && c.extraLabels[key] === label
          ? c
          : {
              ...c,
              extras: { ...c.extras, [key]: value },
              extraLabels: { ...c.extraLabels, [key]: label },
            },
      ),
    [],
  );
  const reset = useCallback(() => {
    setCurrent({
      filters: initial?.filters ?? EMPTY,
      extras: initial?.extras ?? {},
      extraLabels: {},
    });
    setVersion((v) => v + 1);
  }, [initial]);
  const hasDate = DATE_REPORTS.includes(report);
  const snapshot = useCallback(
    (): StandardView => ({
      kind: "standard",
      version: 1,
      report,
      filters: {
        ...current.filters,
        preset: hasDate ? current.filters.preset : "",
      },
      extras: current.extras,
      labels: viewLabels(
        current.filters,
        Object.values(current.extraLabels),
        hasDate,
      ),
    }),
    [current, report, hasDate],
  );
  const value = useMemo(
    () => ({
      report,
      initial,
      saved,
      current,
      setFilters,
      setExtra,
      reset,
      snapshot,
    }),
    [report, initial, saved, current, setFilters, setExtra, reset, snapshot],
  );
  return (
    <SavedViewContext.Provider value={value}>
      <React.Fragment key={version}>{children}</React.Fragment>
    </SavedViewContext.Provider>
  );
};

/**
 * A report's own choice (tab, group by, threshold…) that is saved with the view.
 * Like useState, but starts from the saved view and tells the provider about changes.
 */
export const useViewExtra = <T extends string>(
  key: string,
  fallback: T,
  options: { value: T; label: string }[],
  prefix: string,
): [T, (value: T) => void] => {
  const ctx = useSavedView();
  const saved = ctx?.initial?.extras[key];
  const [value, setValue] = useState<T>(
    options.some((o) => o.value === saved) ? (saved as T) : fallback,
  );
  const label = options.find((o) => o.value === value)?.label ?? value;
  const setExtra = ctx?.setExtra;
  useEffect(() => {
    setExtra?.(key, value, prefix ? `${prefix}: ${label}` : label);
  }, [setExtra, key, value, label, prefix]);
  return [value, setValue];
};
